/**
 * 静态权限配置
 *
 * 权限码与后端 wb_menu 表保持一致（见 wb_menu.csv）：
 *   - 后台菜单/按钮：menu_scope = admin
 *   - 前台菜单/按钮：menu_scope = library
 *
 * 真实登录后权限码从后端拉取并写入 store.user.permissions；
 * 此处仅作为：
 *   1) 全量码常量出口（避免组件硬编码字面量）
 *   2) 未对接后端时的角色预设回退
 *
 * 切换角色：修改 CURRENT_ROLE 即可。
 */

// ============ 后台权限码（admin scope） ============

/** 后台一级目录 */
const ADMIN_DIR_PERMISSIONS = [
  'PLATFORM_OPS',                 // 平台运营
  'PERM',                         // 权限管理
  'LIBRARY_MGMT',                 // 机构管理
  'PREVIEW'                       // 前台预览
]

/** 后台消息中心（按钮） */
const MESSAGE_PERMISSIONS = [
  'MESSAGE.LIST.VIEW',            // 查看
  'MESSAGE.READ',                 // 标已读/全部已读
  'MESSAGE.LANDING'               // 落地跳转
]

/** 后台菜单（页面入口） */
const ADMIN_MENU_PERMISSIONS = [
  'MESSAGE.LIST',                 // 消息中心
  'PUBLISHER.LIST',               // 出版社管理
  'BOOK.LIST',                    // 图书管理
  'AUDIOBOOK.LIST',               // 有声书管理
  'VIDEO.LIST',                   // 视频资源管理
  'READING_CHANNEL.LIST',         // 阅读渠道管理
  'ORDER.LIST',                   // 订单管理
  'ORDER.STATS',                  // 采购统计分析
  'USER.LIST',                    // 用户管理
  'ROLE.LIST',                    // 角色管理
  'MENU.LIST',                    // 菜单管理
  'LIBRARY.LIST',                 // 机构列表
  'PREVIEW.FRONT'                 // 前台预览
]

/** 出版社管理（按钮） */
const PUBLISHER_PERMISSIONS = [
  'PUBLISHER.LIST.VIEW',          // 查看
  'PUBLISHER.CREATE',             // 新增
  'PUBLISHER.UPDATE',             // 编辑
  'PUBLISHER.DELETE',             // 删除
  'PUBLISHER.HP_REC',             // 首页展示位管理（PRD §12.1）
  'PUBLISHER.EXPORT',             // 批量导出（PRD §12.1）
  'PUBLISHER.BATCH_STATUS'        // 批量变更合作状态（PRD §12.1）
]

/** 图书管理（按钮 + 回收站 menu） */
const BOOK_PERMISSIONS = [
  'BOOK.LIST.VIEW',               // 查看
  'BOOK.CREATE',                  // 新增
  'BOOK.UPDATE',                  // 编辑
  'BOOK.UPLOAD',                  // 文件上传/附件
  'BOOK.SHELF',                   // 上架/下架
  'BOOK.DELETE',                  // 删除（软删→回收站）
  'BOOK.EXPORT',                  // 批量导出
  'BOOK.IMPORT',                  // 批量导入（Excel/MARC）
  'BOOK.RECYCLE',                 // 回收站（含恢复）
  'BOOK.PERM_DELETE'              // 彻底删除（物理）
]

/** 视频资源管理（按钮） */
const VIDEO_PERMISSIONS = [
  'VIDEO.LIST.VIEW',              // 查看
  'VIDEO.CREATE',                 // 新增
  'VIDEO.UPDATE',                 // 编辑
  'VIDEO.PUBLISH',                // 上架
  'VIDEO.UNPUBLISH',              // 下架
  'VIDEO.DELETE',                 // 软删除
  'VIDEO.RESTORE',                // 回收站/恢复
  'VIDEO.PURGE',                  // 彻底删除（平台运营独占）
  'VIDEO.IMPORT',                 // 批量导入
  'VIDEO.EPISODE.MANAGE'          // 剧集管理（含单集上传/替换/重抽帧）
]

/** 有声书管理（按钮） */
const AUDIOBOOK_PERMISSIONS = [
  'AUDIOBOOK.LIST.VIEW',          // 查看
  'AUDIOBOOK.CREATE',             // 新增
  'AUDIOBOOK.UPDATE',             // 编辑
  'AUDIOBOOK.PUBLISH',            // 上架
  'AUDIOBOOK.UNPUBLISH',          // 下架
  'AUDIOBOOK.DELETE',             // 软删除
  'AUDIOBOOK.RESTORE',            // 回收站/恢复
  'AUDIOBOOK.PURGE',              // 彻底删除（平台运营独占）
  'AUDIOBOOK.IMPORT',             // 批量导入
  'AUDIOBOOK.CHAPTER.MANAGE'      // 章节管理（含单章音频上传）
]

/** 订单管理（按钮） */
const ORDER_PERMISSIONS = [
  'ORDER.LIST',              // 查看
  'ORDER.LIST'                  // 编辑（含状态变更/物流/签收/导出/问题/批量等）
]

/** 用户管理（按钮） */
const USER_PERMISSIONS = [
  'USER.LIST.VIEW',
  'USER.CREATE',
  'USER.UPDATE',
  'USER.DELETE',
  'USER.RESET_PW',
  'USER.TOGGLE_STATUS',
  'USER.ROLE.ASSIGN'
]

/** 角色管理（按钮） */
const ROLE_PERMISSIONS = [
  'ROLE.LIST.VIEW',
  'ROLE.CREATE',
  'ROLE.UPDATE',
  'ROLE.DELETE',
  'ROLE.ASSIGN_PERM'
]

/** 菜单管理（按钮） */
const MENU_PERMISSIONS = [
  'MENU.LIST.VIEW',
  'MENU.CREATE',
  'MENU.UPDATE',
  'MENU.DELETE',
  'MENU.TOGGLE_STATUS'
]

/** 机构（图书馆）管理（按钮）— 旧码，保留兼容 */
const LIBRARY_PERMISSIONS_LEGACY = [
  'LIBRARY.LIST.VIEW',
  'LIBRARY.CREATE',
  'LIBRARY.UPDATE',
  'LIBRARY.MENU_GRANT'            // 前台菜单授权
]

/** 阅读渠道管理（按钮） */
const READING_CHANNEL_PERMISSIONS = [
  'READING_CHANNEL.LIST.VIEW',    // 查看
  'READING_CHANNEL.CREATE',       // 新增渠道
  'READING_CHANNEL.UPDATE',       // 编辑/启停/归档/清退资源
  'READING_CHANNEL.IMPORT',       // 导入/下载模板/重新匹配
  'READING_CHANNEL.DELETE'        // 删除渠道（仅从未启用且无活动批次）
]

/** 机构管理 — 新 org:* 细粒度权限（对齐 wb_menu V2.93.0） */
const LIBRARY_ORG_PERMISSIONS = [
  'LIBRARY.LIST',                     // 查看机构列表
  'LIBRARY.CREATE',                   // 新增机构
  'LIBRARY.UPDATE',                     // 编辑机构
  'LIBRARY.DELETE',                   // 删除机构
  'LIBRARY.IP_CFG',                       // IP 范围设置
  'LIBRARY.KEYWORD',                  // 关键词反选
  'LIBRARY.HP_REC',                 // 首页展示位管理
  'LIBRARY.WEIGHT',                   // 荐购权重配置
  'LIBRARY.LIST'                       // 操作日志
]

// ============ 前台权限码（library scope） ============

/** 前台一级目录 */
const FRONT_DIR_PERMISSIONS = [
  'F.HOME',                       // 首页
  'F.SUBSCRIBE',                  // 征订荐购
  'F.PROFILE',                    // 个人中心
  'F.LIB_OPS'                     // 馆员专区（librarianOnly）
]

/** 前台菜单 */
const FRONT_MENU_PERMISSIONS = [
  'F.HOME.VIEW',                  // 首页
  'F.SUBSCRIBE.LIST',             // 检索列表
  'F.PROFILE.MY_RECOMMEND',       // 我的荐购
  'F.PROFILE.MY_FAVORITES',       // 我的收藏
  'F.PROFILE.MY_CART',            // 购物车
  'F.USER.ACCOUNT',              // 馆员-用户管理
  'F.USER.ROLE',              // 馆员-角色管理
  'F.ORDER.LIST',             // 馆员-订单管理
  'F.HOLDING.STATS'                // 馆员-馆藏管理
]

/** 前台征订/个人中心（按钮） */
const FRONT_SUBSCRIBE_PERMISSIONS = [
  'F.SUBSCRIBE.LIST.VIEW',        // 查看
  'F.PEB.LIST',        // 纸电同采-浏览
  'F.RECOMMEND.SUBMIT',           // 提交荐购
  'F.RECOMMEND.LIST.CART',        // 加入购物车（btn·PRD §12.1 #p2）
  'F.RECOMMEND.LIST.EXPORT',      // 导出 Excel（btn·PRD §12.1 #p3）
  'F.RECOMMEND.LIST.PURCHASE_FILTER', // 已购/未购筛选（btn·PRD §12.1 #p4）
  'F.PROFILE.MY_RECOMMEND.VIEW',
  'F.PROFILE.MY_RECOMMEND.CANCEL',
  'F.PROFILE.MY_FAVORITES.VIEW',
  'F.PROFILE.MY_FAVORITES.REMOVE'
]

/** 馆员专区-用户管理（按钮） */
const FRONT_LIB_USER_PERMISSIONS = [
  'F.USER.ACCOUNT.VIEW',
  'F.USER.ACCOUNT.STAFF.CREATE',           // 提升馆员
  'F.USER.ACCOUNT.STAFF.REMOVE',            // 取消馆员
  'F.USER.ACCOUNT.READER.IMPORT',            // 批量导入读者
  'F.USER.ACCOUNT.READER.CREATE',     // 新增读者
  'F.USER.ACCOUNT.READER.UPDATE',     // 编辑读者
  'F.USER.ACCOUNT.STAFF.UPDATE',      // 编辑馆员
  'F.USER.ACCOUNT.STAFF.TOGGLE_STATUS',
  'F.USER.ACCOUNT.STAFF.ASSIGN_ROLE',
  'F.USER.ACCOUNT.STAFF.RESET_PWD'
]

/** 馆员专区-角色管理（按钮） */
const FRONT_LIB_ROLE_PERMISSIONS = [
  'F.USER.ROLE.VIEW',
  'F.USER.ROLE.CREATE',
  'F.USER.ROLE.UPDATE',
  'F.USER.ROLE.DELETE',
  'F.USER.ROLE.ASSIGN_PERM',
  'F.USER.ROLE.ASSIGN_USER'              // 分配馆员
]

// ============ 汇总集合 ============

const ALL_ADMIN_PERMISSIONS = [
  ...ADMIN_DIR_PERMISSIONS,
  ...ADMIN_MENU_PERMISSIONS,
  ...MESSAGE_PERMISSIONS,
  ...PUBLISHER_PERMISSIONS,
  ...BOOK_PERMISSIONS,
  ...AUDIOBOOK_PERMISSIONS,
  ...VIDEO_PERMISSIONS,
  ...ORDER_PERMISSIONS,
  ...USER_PERMISSIONS,
  ...ROLE_PERMISSIONS,
  ...MENU_PERMISSIONS,
  ...LIBRARY_PERMISSIONS_LEGACY,
  ...LIBRARY_ORG_PERMISSIONS,
  ...READING_CHANNEL_PERMISSIONS
]

const ALL_FRONT_PERMISSIONS = [
  ...FRONT_DIR_PERMISSIONS,
  ...FRONT_MENU_PERMISSIONS,
  ...FRONT_SUBSCRIBE_PERMISSIONS,
  ...FRONT_LIB_USER_PERMISSIONS,
  ...FRONT_LIB_ROLE_PERMISSIONS
]

// ============ 角色预设（仅供未对接后端时回退） ============

const ROLE_PRESETS = {
  /** 平台管理员 — 全部后台 + 前台权限 */
  PLATFORM_ADMIN: [
    ...ALL_ADMIN_PERMISSIONS,
    ...ALL_FRONT_PERMISSIONS
  ],

  /** 机构管理员（馆员）— 前台全权限 + 受限后台只读 */
  ORG_ADMIN: [
    'PUBLISHER.LIST', 'PUBLISHER.LIST.VIEW',
    'BOOK.LIST', 'BOOK.LIST.VIEW', 'BOOK.RECYCLE',
    'AUDIOBOOK.LIST', 'AUDIOBOOK.LIST.VIEW',
    'VIDEO.LIST', 'VIDEO.LIST.VIEW',
    'ORDER.LIST', 'ORDER.LIST', 'ORDER.LIST',
    'ORDER.STATS',
    ...ALL_FRONT_PERMISSIONS
  ],

  /** 采编员 — 出版社/图书的录入维护 */
  CATALOGER: [
    'PUBLISHER.LIST', 'PUBLISHER.LIST.VIEW', 'PUBLISHER.CREATE', 'PUBLISHER.UPDATE',
    'BOOK.LIST', 'BOOK.LIST.VIEW', 'BOOK.CREATE', 'BOOK.UPDATE',
    'BOOK.UPLOAD', 'BOOK.SHELF', 'BOOK.EXPORT', 'BOOK.IMPORT', 'BOOK.RECYCLE',
    'AUDIOBOOK.LIST', 'AUDIOBOOK.LIST.VIEW', 'AUDIOBOOK.CREATE', 'AUDIOBOOK.UPDATE',
    'AUDIOBOOK.PUBLISH', 'AUDIOBOOK.UNPUBLISH', 'AUDIOBOOK.DELETE',
    'AUDIOBOOK.RESTORE', 'AUDIOBOOK.IMPORT', 'AUDIOBOOK.CHAPTER.MANAGE',
    'VIDEO.LIST', 'VIDEO.LIST.VIEW', 'VIDEO.CREATE', 'VIDEO.UPDATE',
    'VIDEO.PUBLISH', 'VIDEO.UNPUBLISH', 'VIDEO.DELETE',
    'VIDEO.RESTORE', 'VIDEO.IMPORT', 'VIDEO.EPISODE.MANAGE',
    'ORDER.LIST', 'ORDER.LIST',
    'F.HOME', 'F.HOME.VIEW',
    'F.SUBSCRIBE', 'F.SUBSCRIBE.LIST', 'F.SUBSCRIBE.LIST.VIEW',
    'F.PEB.LIST',
    'F.RECOMMEND.SUBMIT', 'F.CART.LIST',
    'F.PROFILE', 'F.PROFILE.MY_RECOMMEND', 'F.PROFILE.MY_RECOMMEND.VIEW',
    'F.PROFILE.MY_FAVORITES', 'F.PROFILE.MY_FAVORITES.VIEW',
    'F.PROFILE.MY_CART'
  ],

  /** 查阅员 — 仅查看 */
  VIEWER: [
    'PUBLISHER.LIST', 'PUBLISHER.LIST.VIEW',
    'BOOK.LIST', 'BOOK.LIST.VIEW', 'BOOK.RECYCLE',
    'READING_CHANNEL.LIST', 'READING_CHANNEL.LIST.VIEW',
    'AUDIOBOOK.LIST', 'AUDIOBOOK.LIST.VIEW',
    'VIDEO.LIST', 'VIDEO.LIST.VIEW',
    'ORDER.LIST', 'ORDER.LIST',
    'F.HOME', 'F.HOME.VIEW',
    'F.SUBSCRIBE', 'F.SUBSCRIBE.LIST', 'F.SUBSCRIBE.LIST.VIEW',
    'F.PEB.LIST',
    'F.PROFILE', 'F.PROFILE.MY_RECOMMEND', 'F.PROFILE.MY_RECOMMEND.VIEW',
    'F.PROFILE.MY_FAVORITES', 'F.PROFILE.MY_FAVORITES.VIEW'
  ]
}

// ============ 当前生效配置 ============

/**
 * 当前角色 — 修改此值切换角色权限
 * 可选值：PLATFORM_ADMIN | ORG_ADMIN | CATALOGER | VIEWER
 */
export const CURRENT_ROLE = 'PLATFORM_ADMIN'

/** 当前生效的权限列表（未登录时作为回退） */
export const PERMISSIONS = [...ROLE_PRESETS[CURRENT_ROLE]]

/** 用户基本信息（静态模拟） */
export const USER_INFO = {
  role: CURRENT_ROLE,
  roles: [CURRENT_ROLE],
  isPlatformAdmin: CURRENT_ROLE === 'PLATFORM_ADMIN'
}

/**
 * 权限判断工具方法（基于静态 PERMISSIONS 的简化版）
 * 注意：业务代码请使用 @/utils/permission 的 hasPermission，它从 store 读取真实权限。
 */
export function hasPermission(code) {
  return PERMISSIONS.includes(code)
}

// 导出分组 / 角色预设 / 全量集合，便于按需引用
export {
  ADMIN_DIR_PERMISSIONS,
  ADMIN_MENU_PERMISSIONS,
  MESSAGE_PERMISSIONS,
  PUBLISHER_PERMISSIONS,
  BOOK_PERMISSIONS,
  AUDIOBOOK_PERMISSIONS,
  VIDEO_PERMISSIONS,
  ORDER_PERMISSIONS,
  USER_PERMISSIONS,
  READING_CHANNEL_PERMISSIONS,
  ROLE_PERMISSIONS,
  MENU_PERMISSIONS,
  LIBRARY_PERMISSIONS_LEGACY,
  LIBRARY_ORG_PERMISSIONS,
  FRONT_DIR_PERMISSIONS,
  FRONT_MENU_PERMISSIONS,
  FRONT_SUBSCRIBE_PERMISSIONS,
  FRONT_LIB_USER_PERMISSIONS,
  FRONT_LIB_ROLE_PERMISSIONS,
  ALL_ADMIN_PERMISSIONS,
  ALL_FRONT_PERMISSIONS,
  ROLE_PRESETS
}
